'use client'

import { DarkButton, LightButton } from "@/ui/Button"
import { isConsentRequiredRegion, useCookieConsentStore } from "@/store/useCookieConsentStore"
import { setFirebaseAnalyticsEnabled } from "@/vendor/firebase/analytics"
import { runWhenQuiet } from "@/util/dom"
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
  cn,
} from "@heroui/react"
import { useTranslations } from "next-intl"
import { useEffect, useState } from "react"

const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME || ''

const ShieldIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
)

/**
 * Cookie 同意管理：
 * - 首次访问（或同意条款版本更新/过期）时，在底部展示同意横幅；
 * - 支持「全部接受 / 仅必要 / 自定义」三种操作，自定义打开偏好设置弹窗；
 * - 同意结果持久化在 cookie 中，并通过 store 对外暴露，用于按需开启分析类 SDK。
 */
export const CookieConsent = () => {
  const t = useTranslations('cookieConsent')

  const decided = useCookieConsentStore((s) => s.decided)
  const analyticsConsent = useCookieConsentStore((s) => s.analytics)
  const settingsOpen = useCookieConsentStore((s) => s.settingsOpen)
  const acceptAll = useCookieConsentStore((s) => s.acceptAll)
  const openSettings = useCookieConsentStore((s) => s.openSettings)
  const closeSettings = useCookieConsentStore((s) => s.closeSettings)

  // 避免 SSR 与客户端 cookie 读取不一致导致的 hydration 警告与横幅闪烁
  const [mounted, setMounted] = useState(false)
  // 是否处于需要事先告知/同意的地区（欧盟/EEA/英国/瑞士等）
  const [requiredRegion, setRequiredRegion] = useState(false)
  useEffect(() => {
    setMounted(true)
    setRequiredRegion(isConsentRequiredRegion())
  }, [])

  // 同意/撤回分析类 cookie 后即时启停 GA4 采集（无需等待页面重新挂载）。
  // 首次启用会加载并执行 gtag.js（长任务），等页面加载完、用户暂停操作时再执行；撤回则立即生效
  useEffect(() => {
    if (!analyticsConsent) {
      setFirebaseAnalyticsEnabled(false)
      return
    }
    return runWhenQuiet(() => setFirebaseAnalyticsEnabled(true))
  }, [analyticsConsent])

  if (!mounted) return null

  // 仅在需要同意的地区、且用户尚未做出选择时展示横幅
  const showBanner = requiredRegion && !decided

  return (
    <>
      {showBanner && (
        <div
          role="dialog"
          aria-label={t('banner.title')}
          className="fixed z-[120] bottom-4 inset-x-4 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[480px] animate__animated animate__fadeInUp"
        >
          <div className="rounded-2xl border border-theme-primary/10 bg-content1 p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 mt-0.5 text-theme-primary/70">
                <ShieldIcon />
              </span>
              <div className="min-w-0">
                <p className="text-[16px] font-bold text-theme-primary">{t('banner.title')}</p>
                <p className="mt-1 text-[13px] leading-relaxed text-theme-primary/60">
                  {t('banner.desc', { name: APP_NAME })}
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <LightButton size="sm" className="flex-1 sm:flex-none sm:mr-auto px-5" onPress={openSettings}>
                {t('banner.customize')}
              </LightButton>
              <DarkButton size="sm" className="flex-1 sm:flex-none px-6" onPress={acceptAll}>
                {t('banner.acceptAll')}
              </DarkButton>
            </div>
          </div>
        </div>
      )}

      <CookiePreferencesModal isOpen={settingsOpen} onClose={closeSettings} />
    </>
  )
}

const CookiePreferencesModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const t = useTranslations('cookieConsent')

  const functional = useCookieConsentStore((s) => s.functional)
  const analytics = useCookieConsentStore((s) => s.analytics)
  const save = useCookieConsentStore((s) => s.save)
  const acceptAll = useCookieConsentStore((s) => s.acceptAll)
  const rejectAll = useCookieConsentStore((s) => s.rejectAll)

  // 弹窗内的本地草稿，点击保存后才写入
  const [draftFunctional, setDraftFunctional] = useState(functional)
  const [draftAnalytics, setDraftAnalytics] = useState(analytics)

  // 每次打开时同步最新已保存值
  useEffect(() => {
    if (isOpen) {
      setDraftFunctional(functional)
      setDraftAnalytics(analytics)
    }
  }, [isOpen, functional, analytics])

  const categories = [
    {
      key: 'necessary',
      title: t('categories.necessary.title'),
      desc: t('categories.necessary.desc'),
      checked: true,
      disabled: true,
      onChange: undefined,
    },
    {
      key: 'functional',
      title: t('categories.functional.title'),
      desc: t('categories.functional.desc'),
      checked: draftFunctional,
      disabled: false,
      onChange: setDraftFunctional,
    },
    {
      key: 'analytics',
      title: t('categories.analytics.title'),
      desc: t('categories.analytics.desc'),
      checked: draftAnalytics,
      disabled: false,
      onChange: setDraftAnalytics,
    },
  ] as const

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" placement="center" size="lg">
      <ModalContent>
        {(close) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <span className="text-[18px] font-bold text-theme-primary">{t('settings.title')}</span>
              <span className="text-[13px] font-normal text-theme-primary/60">{t('settings.desc')}</span>
            </ModalHeader>
            <ModalBody className="gap-3">
              {categories.map((c) => (
                <div
                  key={c.key}
                  className="flex items-start justify-between gap-4 rounded-xl border border-theme-primary/10 p-4"
                >
                  <div className="min-w-0">
                    <p className="text-[15px] font-bold text-theme-primary">{c.title}</p>
                    <p className="mt-1 text-[13px] leading-relaxed text-theme-primary/60">{c.desc}</p>
                  </div>
                  {c.disabled ? (
                    <span className="flex-shrink-0 text-[12px] font-medium text-theme-primary/40 whitespace-nowrap">
                      {t('settings.alwaysOn')}
                    </span>
                  ) : (
                    <Switch
                      isSelected={c.checked}
                      onValueChange={(v) => c.onChange?.(v)}
                      aria-label={c.title}
                      className="flex-shrink-0"
                    />
                  )}
                </div>
              ))}
            </ModalBody>
            <ModalFooter className="flex-wrap gap-2">
              <LightButton
                size="sm"
                className="px-4"
                onPress={() => {
                  rejectAll()
                  close()
                }}
              >
                {t('settings.rejectAll')}
              </LightButton>
              <LightButton
                size="sm"
                className="px-4"
                onPress={() => {
                  acceptAll()
                  close()
                }}
              >
                {t('settings.acceptAll')}
              </LightButton>
              <DarkButton
                size="sm"
                className={cn("px-5 sm:ml-auto")}
                onPress={() => {
                  save({ functional: draftFunctional, analytics: draftAnalytics })
                  close()
                }}
              >
                {t('settings.save')}
              </DarkButton>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
